/**
 * UserFaceFallback — the user-entity fallback rendered by Avatar.
 *
 * Deterministic Facehash SVG colored from the brand palette, with a
 * single-letter "mouth" if the user's name starts with a letter. Hover-driven
 * eye-blink animation lives in a shared <style> tag injected once into
 * document.head.
 *
 * Imported by Avatar.tsx — not intended as a standalone consumer-facing
 * component. Use `<Avatar user={...} />` instead.
 */
import type { BrandAvatarColor } from "@replohq/lattice/components/avatar/brandAvatarColors";

import * as React from "react";

import { getUserBrandAvatarColor } from "@replohq/lattice/components/avatar/brandAvatarColors";
import { cn } from "@replohq/lattice/utils/cn";
import { Facehash } from "facehash";

const USER_FACE_AVATAR_STYLE_ID = "user-face-avatar-style";

const USER_FACE_AVATAR_STYLE_CONTENT = `
@keyframes user-face-avatar-blink {
  0%, 42%, 100% { transform: scaleY(1); }
  22%, 28% { transform: scaleY(0.08); }
}
.user-face-avatar:hover [data-facehash-face] svg > g {
  animation: user-face-avatar-blink 0.8s cubic-bezier(0.45, 0, 0.55, 1) infinite;
  transform-origin: center;
  transform-box: fill-box;
}
@media (prefers-reduced-motion: reduce) {
  .user-face-avatar:hover [data-facehash-face] svg > g {
    animation: none;
  }
}
`;

/**
 * First character of the user's name, uppercased — but only if it's an actual
 * letter. Skips digits and whitespace so a name like "123" or "  " doesn't
 * produce a number/blank initial.
 */
function getDisplayInitial(name: string | null | undefined): string | null {
  const firstChar = (name ?? "").trim().charAt(0).toUpperCase();
  if (firstChar === "" || !/[a-zA-Z]/.test(firstChar)) {
    return null;
  }
  return firstChar;
}

/**
 * Injects the global keyframes + selector for the hover-driven blink animation.
 * Safe on the server (no-ops when document is unavailable). Uses a stable
 * element id so hot-module-reload updates the existing style tag instead of
 * stacking new ones — otherwise old rules linger and animate stale keyframes.
 */
function injectExpressiveEyeStyle() {
  if (typeof document === "undefined") {
    return;
  }
  const existing = document.getElementById(USER_FACE_AVATAR_STYLE_ID);
  let style: HTMLStyleElement;
  if (existing instanceof HTMLStyleElement) {
    style = existing;
  } else {
    style = document.createElement("style");
    style.id = USER_FACE_AVATAR_STYLE_ID;
    document.head.append(style);
  }
  style.textContent = USER_FACE_AVATAR_STYLE_CONTENT;
}

type UserFaceFallbackProps = {
  /**
   * Seed for both the Facehash and the brand-color hash — typically the user
   * id, so the same person renders the same face across surfaces.
   */
  seed: string;
  /** User's display name; first letter becomes the mouth glyph. */
  name?: string | null;
  /** Forwarded to the wrapping div so the outer Avatar can pass image styling. */
  imageClassName?: string;
};

export function UserFaceFallback({
  seed,
  name,
  imageClassName,
}: UserFaceFallbackProps) {
  // eslint-disable-next-line replo/no-use-effect -- DOM side effect on document.head (mounts a shared <style> tag for the hover-blink keyframes); not state sync or data loading, and render-time mutation would break SSR — see https://react.dev/reference/react/useEffect#connecting-to-an-external-system
  React.useEffect(() => {
    injectExpressiveEyeStyle();
  }, []);

  const { background, foreground, needsBorder }: BrandAvatarColor =
    getUserBrandAvatarColor(seed);
  const initial = getDisplayInitial(name);

  return (
    <div
      style={{
        borderRadius: "inherit",
        color: foreground,
        backgroundColor: background,
      }}
      className={cn(
        "user-face-avatar relative flex h-full w-full items-center justify-center",
        needsBorder && "border-0.5 border-border",
        imageClassName,
      )}
    >
      <Facehash
        name={seed}
        size="100%"
        variant="solid"
        colors={[background]}
        interactive={false}
        intensity3d="none"
        enableBlink={false}
        showInitial={false}
        onRenderMouth={
          initial
            ? () => (
                <span style={{ fontSize: "26cqw", lineHeight: 1 }}>
                  {initial}
                </span>
              )
            : undefined
        }
        style={{ transform: "scale(0.7)" }}
      />
    </div>
  );
}
